'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { SmartImage } from './Skeleton';

interface ImageComparisonSliderProps {
  /** Top (clipped) image — shown on the left side of the divider. */
  leftImage: string;
  /** Bottom image — revealed on the right side of the divider. */
  rightImage: string;
  altLeft?: string;
  altRight?: string;
  /** Starting divider position, 0–100. */
  initialPosition?: number;
  /** On first scroll-into-view, sweep the divider once to demonstrate the
   *  before/after (skipped under prefers-reduced-motion). */
  autoSweep?: boolean;
  className?: string;
}

/**
 * Before/after image comparison with a draggable divider. Both images go through
 * next/image (SmartImage, `fill`) so the heavy source images are served as resized
 * AVIF/WebP and lazy-loaded. Mouse drags from anywhere; touch drags only from
 * the divider handle so vertical page scrolling over the image keeps working.
 * Keyboard via arrow keys.
 * With `autoSweep`, the divider animates once when it enters the viewport to hint
 * that it's interactive and reveal both states.
 */
export default function ImageComparisonSlider({
  leftImage,
  rightImage,
  altLeft = '',
  altRight = '',
  initialPosition = 50,
  autoSweep = false,
  className,
}: ImageComparisonSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  // The intro sweep waits for BOTH images to be visible (not just in-view):
  // sweeping over skeletons demonstrated nothing to the user.
  const [inView, setInView] = useState(false);
  const [leftReady, setLeftReady] = useState(false);
  const [rightReady, setRightReady] = useState(false);
  const sweepDone = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sweepTimers = useRef<number[]>([]);

  // Stable identities so SmartImage's onReady effect doesn't re-fire on every
  // slider-position re-render.
  const markLeftReady = useCallback(() => setLeftReady(true), []);
  const markRightReady = useCallback(() => setRightReady(true), []);

  function cancelSweep() {
    sweepTimers.current.forEach(clearTimeout);
    sweepTimers.current = [];
    setSweeping(false);
  }

  function moveTo(clientX: number) {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.max(0, Math.min(100, pct)));
  }

  function startDrag() {
    cancelSweep(); // user interaction wins over the intro animation
    sweepDone.current = true; // …and a pending (not-yet-started) sweep is off too
    setIsDragging(true);
  }

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e: MouseEvent) => moveTo(e.clientX);
    const onTouchMove = (e: TouchEvent) => moveTo(e.touches[0].clientX);
    const onEnd = () => setIsDragging(false);

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchend', onEnd);
    document.body.style.cursor = 'ew-resize';

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchend', onEnd);
      document.body.style.cursor = '';
    };
  }, [isDragging]);

  // Track the slider entering the viewport (the sweep itself also waits for the
  // images — see the next effect).
  useEffect(() => {
    if (!autoSweep) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        setInView(true);
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [autoSweep]);

  // One-time intro sweep — starts only when the slider is in view AND both
  // images have actually rendered (loaded / cached / errored-out).
  useEffect(() => {
    if (!autoSweep || !inView || !leftReady || !rightReady || sweepDone.current) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    sweepDone.current = true;
    setSweeping(true);
    const push = (fn: () => void, ms: number) =>
      sweepTimers.current.push(window.setTimeout(fn, ms));
    push(() => setSliderPosition(72), 350);
    push(() => setSliderPosition(28), 1100);
    push(() => setSliderPosition(50), 1850);
    push(() => setSweeping(false), 2650);
    return () => {
      sweepTimers.current.forEach(clearTimeout);
      sweepTimers.current = [];
    };
  }, [autoSweep, inView, leftReady, rightReady]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowLeft') setSliderPosition((p) => Math.max(0, p - 2));
    if (e.key === 'ArrowRight') setSliderPosition((p) => Math.min(100, p + 2));
  }

  // While sweeping, tween the clip + handle between keyframes; instant during drag.
  const sweepEase = 'cubic-bezier(0.22, 1, 0.36, 1)';

  return (
    <div
      ref={containerRef}
      className={clsx('group relative h-full w-full select-none overflow-hidden', className)}
      onMouseDown={startDrag}
    >
      {/* Right image — bottom layer */}
      <SmartImage
        src={rightImage}
        alt={altRight}
        fill
        sizes="(max-width: 1024px) 100vw, 28rem"
        draggable={false}
        className="pointer-events-none object-cover"
        onReady={markRightReady}
      />

      {/* Left image — top layer, clipped to the divider */}
      <div
        className="pointer-events-none absolute inset-0 h-full w-full overflow-hidden"
        style={{
          clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)`,
          transition: sweeping ? `clip-path 750ms ${sweepEase}` : undefined,
        }}
      >
        <SmartImage
          src={leftImage}
          alt={altLeft}
          fill
          sizes="(max-width: 1024px) 100vw, 28rem"
          draggable={false}
          className="object-cover"
          onReady={markLeftReady}
        />
      </div>

      {/* Divider + handle — a 48px-wide strip that is the ONLY touch-draggable
          area (touches on the images scroll the page normally). touchAction:none
          stops the browser from scrolling while the handle itself is dragged. */}
      <div
        className="absolute top-0 h-full w-12 -translate-x-1/2 cursor-ew-resize"
        style={{
          left: `${sliderPosition}%`,
          touchAction: 'none',
          transition: sweeping ? `left 750ms ${sweepEase}` : undefined,
        }}
        onTouchStart={startDrag}
      >
        <div className="absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 bg-on-surface/60 backdrop-blur-sm" />

        <button
          type="button"
          onKeyDown={onKeyDown}
          className={clsx(
            'absolute left-1/2 top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-surface/60 text-primary backdrop-blur-md',
            'border border-on-surface/30 transition-transform duration-300 ease-in-out',
            'group-hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            (isDragging || sweeping) && 'scale-105'
          )}
          role="slider"
          aria-valuenow={Math.round(sliderPosition)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-orientation="horizontal"
          aria-label="Image comparison slider"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
